import {Component, ChangeDetectionStrategy, ChangeDetectorRef} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';

import {EMPTY} from 'rxjs';
import {catchError, finalize, takeUntil, tap} from 'rxjs/operators';

import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {Destroyable} from '../../../destroyable.component';
import {ModalService} from '../../../shared/modal';
import {AiGenerateApiResponse, AiGenerateResult} from '../../template-management.interfaces';
import {TemplateManagementService} from '../../template-management.service';

@Component({
  selector: 'gp-ai-generate-modal',
  templateUrl: './ai-generate-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AiGenerateModalComponent extends Destroyable {

  public aiForm: UntypedFormGroup;
  public generating = false;
  public errorMessage: string;

  public templateName: string;
  public templateLanguage: string;
  public existingSfdt: string;
  public onGenerated: (result: AiGenerateResult) => void;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private templateManagementService: TemplateManagementService,
    private modalService: ModalService,
    private toastService: ToastrService,
    private translate: TranslateService,
    private cd: ChangeDetectorRef
  ) {
    super();
    this.aiForm = this.formBuilder.group({
      description: ['', [Validators.required, Validators.maxLength(2500)]]
    });
  }

  public generate(): void {
    if (this.aiForm.invalid) {
      return;
    }

    this.generating = true;
    this.errorMessage = '';

    const request: { name: string, language: string, description: string, existingSfdt?: string } = {
      name: this.templateName,
      language: this.templateLanguage,
      description: this.aiForm.get('description')?.value
    };

    if (this.existingSfdt) {
      request.existingSfdt = this.existingSfdt;
    }

    this.templateManagementService.generateAiTemplate(request)
      .pipe(
        tap((response: AiGenerateApiResponse) => {
          this.modalService.closeTopmostModal();
          if (this.onGenerated) {
            this.onGenerated({
              sfdt: response?.sfdt,
              preservedFields: response?.preservedFields ?? [],
              lostImages: response?.lostImages ?? 0
            });
          }
        }),
        catchError((error) => {
          if (error?.error?.code === 'OFFENSIVE_LANGUAGE') {
            this.errorMessage = this.translate.instant('TEMPLATE.ai_offensive_language');
          } else {
            this.errorMessage = error?.error?.message || this.translate.instant('COMMON.something_went_wrong');
          }
          return EMPTY;
        }),
        finalize(() => {
          this.generating = false;
          this.cd.markForCheck();
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }
}
